import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import './AdminDarkSelect.css';

function normalizeOptions(options) {
  return options.map(option =>
    typeof option === 'string'
      ? { value: option, label: option }
      : { value: option.value, label: option.label ?? String(option.value) }
  );
}

function AdminDarkSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = '',
  required = false,
  allowCustom = true,
  disabled = false,
}) {
  const listId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const normalized = useMemo(() => normalizeOptions(options), [options]);

  const selectedLabel = useMemo(() => {
    const match = normalized.find(option => option.value === value);
    if (match) return match.label;
    return allowCustom ? value || '' : '';
  }, [normalized, value, allowCustom]);

  const [query, setQuery] = useState(selectedLabel);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    function handleOutside(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setQuery(selectedLabel);
      }
    }

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [selectedLabel]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(option => option.label.toLowerCase().includes(q));
  }, [normalized, query]);

  const commit = option => {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
  };

  return (
    <div className="admin-field admin-dark-select" ref={wrapRef}>
      {label && <label className="admin-label">{label}</label>}
      <input
        className="admin-input admin-dark-select-input"
        value={query}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        onFocus={() => {
          if (!disabled) setOpen(true);
        }}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
        onChange={event => {
          const next = event.target.value;
          setQuery(next);
          setOpen(true);
          if (allowCustom) {
            onChange(next);
          }
        }}
        onBlur={() => {
          if (!allowCustom) {
            const match = normalized.find(
              option => option.label.toLowerCase() === query.trim().toLowerCase()
            );
            if (match) {
              commit(match);
            } else {
              setQuery(selectedLabel);
            }
          }
        }}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            setOpen(false);
            setQuery(selectedLabel);
          }
          if (event.key === 'Enter' && filtered.length === 1) {
            event.preventDefault();
            commit(filtered[0]);
          }
        }}
      />

      {open && !disabled && (
        <ul id={listId} className="admin-dark-select-menu" role="listbox">
          {filtered.length === 0 ? (
            <li className="admin-dark-select-empty">No matches</li>
          ) : (
            filtered.map(option => (
              <li key={String(option.value)}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`admin-dark-select-option${option.value === value ? ' active' : ''}`}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => commit(option)}
                >
                  {option.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export default AdminDarkSelect;
